<template>
  <ExternalPage :show-nav="true">
    <div class="max-w-4xl mx-auto p-6 space-y-6">
      <header>
        <h1 class="text-2xl font-bold text-gray-800">MCP Tokens — All Users</h1>
        <p class="mt-2 text-gray-600">
          Every Aerocle MCP token across the team. Revoke any token to
          immediately cut off the Claude connection using it (e.g. a lost
          laptop or a departing teammate).
        </p>
      </header>

      <section class="rounded-lg border border-gray-200 p-5">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-semibold text-gray-800">{{ tokens.length }} token(s)</h2>
          <base-button-affirmative @click="fetchTokens">Refresh</base-button-affirmative>
        </div>

        <p v-if="loading" class="text-gray-500 text-sm">Loading…</p>
        <p v-else-if="!tokens.length" class="text-gray-500 text-sm">No MCP tokens.</p>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-left text-gray-500 border-b">
              <th class="py-2 pr-4">User</th>
              <th class="py-2 pr-4">Label</th>
              <th class="py-2 pr-4">Created</th>
              <th class="py-2 pr-4">Expires</th>
              <th class="py-2"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in tokens" :key="t.id" class="border-b last:border-0 align-top">
              <td class="py-2 pr-4">
                <div class="font-medium text-gray-800">{{ userName(t.user) }}</div>
                <div class="text-gray-500">{{ t.user && t.user.email }}</div>
              </td>
              <td class="py-2 pr-4">{{ t.label || "—" }}</td>
              <td class="py-2 pr-4">{{ formatDate(t.created_at) }}</td>
              <td class="py-2 pr-4">{{ t.expiry ? formatDate(t.expiry) : "expired" }}</td>
              <td class="py-2 text-right">
                <base-button-negative @click="revoke(t)">Revoke</base-button-negative>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>
  </ExternalPage>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import ExternalPage from "../external/base/ExternalPage.vue";
import { listAllMCPTokens, revokeAnyMCPToken } from "@/services/MCPToken";

const store = useStore();
const $toast = useToast();
const authToken = computed(() => store.getters["auth/showToken"]);

const tokens = ref([]);
const loading = ref(false);

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
}

function userName(u) {
  if (!u) return "—";
  const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
  return name || u.username || `User ${u.id}`;
}

async function fetchTokens() {
  loading.value = true;
  const res = await listAllMCPTokens(authToken.value);
  loading.value = false;
  if (res?.status === 200) {
    tokens.value = res.data;
  } else {
    $toast.error("Couldn't load MCP tokens.");
  }
}

async function revoke(t) {
  const who = userName(t.user);
  if (!window.confirm(`Revoke ${who}'s token "${t.label || t.id}"? Their Claude connection using it will stop working.`)) return;
  const res = await revokeAnyMCPToken(authToken.value, t.id);
  if (res?.status === 204) {
    $toast.success("Token revoked.");
    fetchTokens();
  } else {
    $toast.error("Couldn't revoke the token.");
  }
}

onMounted(fetchTokens);
</script>
